import React, { useMemo, useState } from 'react';
import { Plus, Trash2, CheckCircle2, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import InputMonto from '../../components/InputMonto';
import { money, hoy } from './ui';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const TIPOS = [['abono', 'Abono mensual'], ['anual', 'Renovación anual'], ['unico', 'Pago único'], ['proyecto', 'Proyecto']];
const HOSTING = 'Hosting anual (3 casillas de correo + SSL)';

// Reparte un importe en cuotas parejas, dejando el resto en la primera
const repartir = (total, n) => {
  const base = Math.floor(Number(total || 0) / n);
  return Array.from({ length: n }, (_, i) => (i === 0 ? Number(total) - base * (n - 1) : base));
};
const sumarMeses = (f, m) => { const d = new Date(f + 'T12:00:00'); d.setMonth(d.getMonth() + m); return d.toISOString().slice(0, 10); };

/*
 Alta de un trabajo: el desarrollo con su plan de pagos, el hosting anual y
 los servicios mensuales. Se usa al cerrar una propuesta y desde la ficha del cliente.
*/
export default function ModalTrabajo({ cliente_id, clienteNombre, nombreInicial = '', montoInicial = '', propuestaId = null, onClose, onListo }) {
  const [nombre, setNombre] = useState(nombreInicial);
  const [crearProyecto, setCrearProyecto] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [dev, setDev] = useState({ activo: true, nombre: 'Desarrollo web', monto: montoInicial || '', cuotas: 2, primera: hoy() });
  const [hosting, setHosting] = useState({ activo: false, nombre: HOSTING, monto: '', mes: String(new Date().getMonth() + 1), conPrimerPago: true });
  const [soporte, setSoporte] = useState({ activo: false, nombre: 'Soporte y mantenimiento', monto: '', dia: 10 });
  const [extras, setExtras] = useState([]);

  // Plan de pagos que se va a crear: la primera cuota queda habilitada y el resto "a pasar"
  const plan = useMemo(() => {
    const filas = [];
    const montos = dev.activo && Number(dev.monto) ? repartir(dev.monto, Number(dev.cuotas) || 1) : [];
    montos.forEach((m, i) => filas.push({
      concepto: montos.length > 1 ? `${dev.nombre} ${i + 1}/${montos.length}` : dev.nombre,
      monto: m, vencimiento: i === 0 ? dev.primera : sumarMeses(dev.primera, i), habilitado: i === 0,
    }));
    if (hosting.activo && Number(hosting.monto)) {
      if (hosting.conPrimerPago && filas.length) {
        filas[0] = { ...filas[0], monto: Number(filas[0].monto) + Number(hosting.monto), concepto: filas[0].concepto + ' + hosting' };
      } else {
        filas.push({ concepto: 'Hosting anual', monto: Number(hosting.monto), vencimiento: dev.primera, habilitado: true });
      }
    }
    return filas;
  }, [dev, hosting]);

  const servicios = useMemo(() => {
    const s = [];
    if (dev.activo && Number(dev.monto)) s.push({ nombre: dev.nombre, tipo: 'proyecto', monto: Number(dev.monto) });
    if (hosting.activo && Number(hosting.monto)) s.push({ nombre: hosting.nombre, tipo: 'anual', monto: Number(hosting.monto), mes_renovacion: hosting.mes });
    if (soporte.activo && Number(soporte.monto)) s.push({ nombre: soporte.nombre, tipo: 'abono', monto: Number(soporte.monto), dia_cobro: soporte.dia });
    extras.forEach(e => { if (e.nombre && Number(e.monto)) s.push({ ...e, monto: Number(e.monto) }); });
    return s;
  }, [dev, hosting, soporte, extras]);

  const guardar = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return toast.error('Ponele un nombre al trabajo');
    if (!servicios.length) return toast.error('Cargá al menos un servicio');
    setGuardando(true);
    try {
      const cuerpo = { cliente_id, nombre, servicios, plan_pago: plan, crear_proyecto: crearProyecto };
      const { data } = propuestaId
        ? await api.post(`/agency/propuestas/${propuestaId}/cerrar`, cuerpo)
        : await api.post('/agency/trabajos', cuerpo);
      const futuros = plan.filter(p => !p.habilitado).length;
      toast.success(`Listo: ${data.contratos.length} servicios y ${data.cobros.length} pagos` + (futuros ? ` (${futuros} quedan a pasar)` : ''), { duration: 7000 });
      onListo && onListo(data);
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo crear'); }
    finally { setGuardando(false); }
  };

  const setExtra = (i, campo, v) => setExtras(extras.map((x, j) => (j === i ? { ...x, [campo]: v } : x)));
  const bloque = (activo) => ({ border: '1px solid ' + (activo ? 'var(--accent)' : 'var(--border)'), borderRadius: 12, padding: 14, marginBottom: 12 });
  const check = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h2>Nuevo trabajo{clienteNombre ? ` · ${clienteNombre}` : ''}</h2>
        <form onSubmit={guardar}>
          <div className="form-group"><label>Nombre del trabajo</label>
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Sitio institucional" required autoFocus /></div>

          {/* Desarrollo */}
          <div style={bloque(dev.activo)}>
            <label style={check}>
              <input type="checkbox" checked={dev.activo} onChange={e => setDev({ ...dev, activo: e.target.checked })} style={{ width: 16, height: 16 }} />
              Desarrollo / trabajo principal
            </label>
            {dev.activo && (
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Concepto</label>
                  <input value={dev.nombre} onChange={e => setDev({ ...dev, nombre: e.target.value })} /></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Importe total</label>
                  <InputMonto value={dev.monto} onChange={v => setDev({ ...dev, monto: v })} /></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Se cobra en</label>
                  <select value={dev.cuotas} onChange={e => setDev({ ...dev, cuotas: e.target.value })}>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} {n === 1 ? 'pago' : 'pagos'}</option>)}
                  </select></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Primer pago</label>
                  <input type="date" value={dev.primera} onChange={e => setDev({ ...dev, primera: e.target.value })} /></div>
              </div>
            )}
          </div>

          {/* Hosting */}
          <div style={bloque(hosting.activo)}>
            <label style={check}>
              <input type="checkbox" checked={hosting.activo} onChange={e => setHosting({ ...hosting, activo: e.target.checked })} style={{ width: 16, height: 16 }} />
              Hosting anual
            </label>
            {hosting.activo && (<>
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Concepto</label>
                  <input value={hosting.nombre} onChange={e => setHosting({ ...hosting, nombre: e.target.value })} /></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Importe anual</label>
                  <InputMonto value={hosting.monto} onChange={v => setHosting({ ...hosting, monto: v })} /></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Mes de renovación</label>
                  <select value={hosting.mes} onChange={e => setHosting({ ...hosting, mes: e.target.value })}>
                    {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select></div>
              </div>
              <label style={{ ...check, marginTop: 10, fontWeight: 400, fontSize: 13 }}>
                <input type="checkbox" checked={hosting.conPrimerPago} onChange={e => setHosting({ ...hosting, conPrimerPago: e.target.checked })} style={{ width: 16, height: 16 }} />
                Cobrarlo completo junto al primer pago
              </label>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Queda cargado como renovación anual: vence el último día de {MESES[Number(hosting.mes) - 1]} y te avisa a vos el día 1 para revisar el sitio antes de pasarlo.
              </div>
            </>)}
          </div>

          {/* Soporte mensual */}
          <div style={bloque(soporte.activo)}>
            <label style={check}>
              <input type="checkbox" checked={soporte.activo} onChange={e => setSoporte({ ...soporte, activo: e.target.checked })} style={{ width: 16, height: 16 }} />
              Soporte o abono mensual
            </label>
            {soporte.activo && (
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Concepto</label>
                  <input value={soporte.nombre} onChange={e => setSoporte({ ...soporte, nombre: e.target.value })} /></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Importe mensual</label>
                  <InputMonto value={soporte.monto} onChange={v => setSoporte({ ...soporte, monto: v })} /></div>
                <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Día de cobro</label>
                  <input type="number" min="1" max="31" value={soporte.dia} onChange={e => setSoporte({ ...soporte, dia: e.target.value })} /></div>
              </div>
            )}
          </div>

          {/* Otros servicios */}
          {extras.map((x, i) => (
            <div key={i} style={{ ...bloque(true), display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, alignItems: 'end' }}>
              <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Servicio</label>
                <input value={x.nombre} onChange={e => setExtra(i, 'nombre', e.target.value)} placeholder="Google Ads" /></div>
              <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tipo</label>
                <select value={x.tipo} onChange={e => setExtra(i, 'tipo', e.target.value)}>{TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              <div><label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Importe</label>
                <InputMonto value={x.monto} onChange={v => setExtra(i, 'monto', v)} /></div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExtras(extras.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExtras([...extras, { nombre: '', tipo: 'abono', monto: '', dia_cobro: 10 }])}>
            <Plus size={14} /> Agregar otro servicio
          </button>

          {/* Vista previa del plan */}
          {plan.length > 0 && (
            <div style={{ marginTop: 18, border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Pagos que se van a crear</div>
              {plan.map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', fontSize: 13, opacity: c.habilitado ? 1 : 0.6 }}>
                  <span>{c.habilitado ? '' : <Lock size={12} style={{ marginRight: 4 }} />}{c.concepto}
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · {c.habilitado ? 'cobrable ahora' : 'a pasar'}</span></span>
                  <b>{money(c.monto)}</b>
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                Las cuotas siguientes quedan "a pasar": no le aparecen como pagables al cliente ni cuentan como facturado hasta que las habilites.
              </div>
            </div>
          )}

          <label style={{ ...check, fontWeight: 400, fontSize: 13, margin: '16px 0' }}>
            <input type="checkbox" checked={crearProyecto} onChange={e => setCrearProyecto(e.target.checked)} style={{ width: 16, height: 16 }} />
            Crear el proyecto para seguir los avances (lo ve el cliente en su portal)
          </label>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}><CheckCircle2 size={15} /> {guardando ? 'Creando...' : 'Crear el trabajo'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
